import { useEffect, useState } from 'react';

import { MfaCard } from './MfaCard';
import { AccountPanel } from './AccountPanel';
import { BrandingPanel } from '../branding';
import { CustomDomainPanel } from '../domains';
import type { Source, StatusCounts, Summary, Target } from '../types';
import { SOURCE_LABELS } from '../types';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { tally, hiddenReason, groupHidden, CATEGORY_LABEL, CATEGORY_NOTE } from '../visibility';
import { fetchRemote, type Remote } from '../update-check';

interface Props {
  summary: Summary | null;
  counts: { overall: StatusCounts; bySource: Record<Source, StatusCounts> };
  /** Excluded from every count and from the table - see visibility.ts. */
  email: string | null;
  orgId: string | null;
  /**
   * Three separate answers, not one.
   *
   * A person may own the logo without going near DNS, and the decision to
   * enforce two-factor across the company usually belongs to neither of them.
   * The panels are UNMOUNTED rather than shown read-only: a greyed-out control
   * still tells a tech that a thing exists and invites the "why can't I" ticket,
   * and there is nothing on these three panels worth reading if you cannot act.
   *
   * MfaCard is the exception - it always renders, because it also holds a
   * person's OWN enrolment. See the note there.
   */
  canBranding: boolean;
  canDomain: boolean;
  canSecurity: boolean;
  onSignOut: () => void;
}

/**
 * Settings is deliberately mostly read-only.
 *
 * The thresholds and schedules live in the collectors, where they are shared
 * between Datto and ShadowControl and enforced server-side. Surfacing them as
 * editable here would imply the UI is the source of truth, which it is not -
 * and a mis-set threshold silently turns real failures green, which is the
 * exact class of bug this rewrite existed to fix.
 */
export function Settings({
  summary, counts, email, orgId, canBranding, canDomain, canSecurity, onSignOut,
}: Props) {
  const sources: Source[] = ['datto', 'sp', 'sc', 'msp', 'unitrends', 'email'];

  return (
    <div className="settings">
      <BuildPanel />


      {canBranding && <BrandingPanel orgId={orgId} canManage={canBranding} />}

      {/* Directly after branding: the logo just uploaded is exactly what appears
          on the address claimed here, so the two belong on one screen. */}
      {canDomain && <CustomDomainPanel orgId={orgId} canManage={canDomain} />}


      <section className="panel">
        <h2 className="panel-title">Sync health</h2>
        <p className="panel-note">
          Datto runs every 15 minutes; ShadowControl runs at :07, :22, :37 and :52.
          They are offset so a slow Datto sweep is not competing with a ShadowControl
          login for a cold start.
        </p>
        <table className="mini-table">
          <thead>
            <tr><th>Source</th><th>Last run</th><th>Result</th><th>Data from</th><th>Machines</th></tr>
          </thead>
          <tbody>
            {sources.map((s) => {
              // THESE THREE MUST DESCRIBE THE SAME RUN. This table used to pair
              // lastSyncedAt (the last successful WRITE) with lastSyncOk (the
              // last ATTEMPT), which rendered rows like "2:07:05 PM - Failed"
              // for a 2:07 run that succeeded, because the failure came at 2:22
              // and wrote nothing. "Data from" is the separate, genuinely
              // useful question of how stale the numbers on screen are.
              const ok = summary?.lastSyncOk?.[s];
              const ranAt = summary?.lastRunAt?.[s] ?? null;
              const err = summary?.lastRunError?.[s] ?? null;
              const dataAt = summary?.lastSyncedAt?.[s] ?? null;
              const c = counts.bySource[s];
              const stale = !ok && dataAt?.toDate && ranAt?.toDate
                && dataAt.toDate().getTime() < ranAt.toDate().getTime();
              return (
                <tr key={s}>
                  <td>{SOURCE_LABELS[s]}</td>
                  <td className="mono dim">
                    {ranAt?.toDate ? ranAt.toDate().toLocaleString() : '--'}
                  </td>
                  <td>
                    <span className={'tag ' + (ok ? 'tag-ok' : 'tag-bad')} title={err ?? undefined}>
                      {ok ? 'OK' : 'Failed'}
                    </span>
                    {err && <div className="sync-err" title={err}>{err}</div>}
                  </td>
                  <td className="mono dim">
                    {dataAt?.toDate ? dataAt.toDate().toLocaleString() : '--'}
                    {stale && (
                      <span className="sync-stale" title="The last attempt failed, so what you are looking at is older than it appears.">
                        stale
                      </span>
                    )}
                  </td>
                  <td className="mono">{c.total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="panel">
        <h2 className="panel-title">Status thresholds</h2>
        <p className="panel-note">
          Set in the collectors and applied identically to all three sources. A status is
          always the <em>worst</em> of the vendor's own flag and the age of the last
          successful backup — a vendor reporting "OK" can never mask a stale backup.
        </p>
        <dl className="kv">
          <dt>Healthy</dt>
          <dd>Backed up within {summary?.thresholds?.warnHours ?? 26} hours</dd>
          <dt>Warning</dt>
          <dd>
            Between {summary?.thresholds?.warnHours ?? 26} and{' '}
            {summary?.thresholds?.failHours ?? 48} hours
          </dd>
          <dt>Failed</dt>
          <dd>
            Last run failed, or older than {summary?.thresholds?.failHours ?? 48} hours
          </dd>
          <dt>Paused</dt>
          <dd>Paused or archived by an operator; excluded from the device rollup</dd>
          <dt>Unknown</dt>
          <dd>No data — no agents, no history, or the vendor API call failed</dd>
        </dl>
      </section>


      {/* Your account, then your second factor. Grouped because they are the
          things about YOU on a page otherwise about the organisation. */}
      <AccountPanel email={email} onSignOut={onSignOut} />

      <MfaCard orgId={orgId} canManage={canSecurity} />
    </div>
  );
}

/**
 * What is being excluded, and why.
 *
 * The exclusion rule is blunt on purpose (see visibility.ts), so it is reported
 * here rather than applied invisibly - if it ever starts hiding something that
 * matters, this panel is where you find out.
 */
export function HiddenPanel({ hidden, orgId, canManage }: {
  hidden: Target[]; orgId: string | null; canManage: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  /**
   * A retired machine needs RESTORING, not unhiding - they are different
   * fields and different meanings. Calling setTargetHidden on one would clear a
   * flag it never had and leave it retired, i.e. a button that appears to work
   * and does nothing.
   */
  async function restore(t: Target) {
    setBusy(t.id); setErr(null);
    try {
      await httpsCallable(functions, 'setTargetRetired')({ orgId, targetId: t.id, retired: false });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not restore that machine.');
    } finally { setBusy(null); }
  }

  async function unhide(t: Target) {
    if (!orgId) return;
    setBusy(t.id); setErr(null);
    try {
      await httpsCallable(functions, 'setTargetHidden')({ orgId, targetId: t.id, hidden: false });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not unhide that machine.');
    } finally { setBusy(null); }
  }

  if (hidden.length === 0) return null;
  const groups = groupHidden(hidden);
  const counts = tally(hidden);

  return (
    <section className="panel">
      <h2 className="panel-title">Hidden from this dashboard</h2>
      <p className="panel-note">
        {hidden.length} {hidden.length === 1 ? 'machine is' : 'machines are'} excluded from the
        table and from every count.{' '}
        {counts.failed > 0 && (
          <strong style={{ color: 'var(--red)' }}>
            {counts.failed} of {hidden.length === 1 ? 'them is' : 'them are'} failing.
          </strong>
        )}
      </p>
      {err && <div className="banner banner-error">{err}</div>}

      {groups.map(({ category, items }) => (
        <div key={category} className="hidden-group">
          <div className="hidden-group-hd">
            <span className="hidden-group-name">{CATEGORY_LABEL[category]}</span>
            <span className="mono dim">{items.length}</span>
          </div>
          <p className="panel-note">{CATEGORY_NOTE[category]}</p>
          <table className="mini-table">
            <thead><tr><th>Machine</th><th>Status</th><th>Detail</th><th /></tr></thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div className="mono">{t.name}</div>
                    <div className="cell-sub">{t.client || 'No client assigned'}</div>
                  </td>
                  <td><StatusTag status={t.status} /></td>
                  <td className="dim" style={{ fontSize: 11.5 }}>
                    {hiddenReason(t)}
                    {t.statusReason && <div className="cell-sub">{t.statusReason}</div>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {canManage && (
                      category === 'retired' ? (
                        // Retired machines are restored, not unhidden: the two
                        // set different fields, and unhiding one would clear a
                        // flag it never had while leaving it retired.
                        <button className="btn-link" disabled={busy === t.id}
                                title="Put this machine back on the board"
                                onClick={() => void restore(t)}>
                          {busy === t.id ? 'restoring' : 'restore'}
                        </button>
                      ) : (
                        <button className="btn-link" disabled={busy === t.id}
                                onClick={() => void unhide(t)}>
                          {busy === t.id ? 'unhiding' : 'unhide'}
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {!canManage && (
        <p className="panel-note">Only an owner or admin can unhide a machine.</p>
      )}
    </section>
  );
}

/** Small status chip, so a hidden failure still reads as a failure. */
function StatusTag({ status }: { status: string }) {
  const cls =
    status === 'failed' ? 'tag-bad'
      : status === 'healthy' ? 'tag-ok'
        : 'tag-warn';
  return <span className={'tag ' + cls}>{status}</span>;
}

/**
 * Build information plus a manual update check.
 *
 * Worth knowing when reading this on screen: the panel you are looking at was
 * rendered by the build it is reporting on. If a deploy changed this panel, you
 * will not see the change until after the update lands.
 */
function BuildPanel() {
  const [remote, setRemote] = useState<Remote | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  const check = async () => {
    setChecking(true);
    setRemote(await fetchRemote());
    setCheckedAt(new Date());
    setChecking(false);
  };

  useEffect(() => { void check(); }, []);

  const upToDate = remote ? remote.buildId === __BUILD_ID__ : null;

  return (
    <section className="panel">
      <h2 className="panel-title">Build</h2>
      <dl className="kv">
        <dt>Version</dt>
        <dd className="mono">v{__APP_VERSION__}</dd>
        <dt>Build ID</dt>
        <dd className="mono">{__BUILD_ID__}</dd>
        <dt>Built</dt>
        <dd className="mono dim">{new Date(__BUILT_AT__).toLocaleString()}</dd>
        <dt>Deployed</dt>
        <dd>
          {remote === null ? (
            <span className="dim">{checking ? 'checking…' : 'could not reach /version.json'}</span>
          ) : upToDate ? (
            <span className="tag tag-ok">Up to date</span>
          ) : (
            <span className="tag tag-warn">v{remote.version} available</span>
          )}
        </dd>
      </dl>
      <div className="panel-actions">
        <button className="btn" onClick={() => void check()} disabled={checking}>
          {checking ? 'Checking…' : 'Check for updates'}
        </button>
        {checkedAt && (
          <span className="dim mono panel-hint">
            last checked {checkedAt.toLocaleTimeString()}
          </span>
        )}
      </div>
      {upToDate === false && (
        <p className="panel-note">
          A newer build is deployed. The update prompt appears automatically within
          five minutes, or when you next focus this tab.
        </p>
      )}
    </section>
  );
}
